import React, { useState, useEffect } from "react";

interface MeetingRoomDateTimeSelectorProps {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
}

// Helper function to get rounded up hour
const getRoundedUpTime = () => {
  const now = new Date();
  const currentMinutes = now.getMinutes();
  
  if (currentMinutes > 0) {
    now.setHours(now.getHours() + 1);
  }
  now.setMinutes(0);
  now.setSeconds(0);
  
  const hours = now.getHours().toString().padStart(2, '0');
  return `${hours}:00`;
};

// Helper function to add hours to a time string
const addHoursToTime = (dateStr: string, timeStr: string, hoursToAdd: number) => {
  const date = new Date(dateStr);
  const [hours, minutes] = timeStr.split(':').map(Number);
  date.setHours(hours, minutes, 0, 0);
  date.setHours(date.getHours() + hoursToAdd);
  return date;
};

export function MeetingRoomDateTimeSelector({
  startDate,
  endDate,
  startTime,
  endTime,
  onStartDateChange,
  onEndDateChange,
  onStartTimeChange,
  onEndTimeChange,
}: MeetingRoomDateTimeSelectorProps) {
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize times on mount if not provided
  useEffect(() => {
    if (!startTime && !isInitialized) {
      const roundedStartTime = getRoundedUpTime();
      
      setTimeout(() => {
        onStartTimeChange(roundedStartTime);
        
        // Auto-calculate end time (1 hour later to ensure minimum duration)
        const currentStartDate = startDate || new Date().toISOString().split('T')[0];
        const endDateTime = addHoursToTime(currentStartDate, roundedStartTime, 1);
        const endHours = endDateTime.getHours().toString().padStart(2, '0');
        
        onEndDateChange(endDateTime.toISOString().split('T')[0]);
        onEndTimeChange(`${endHours}:00`);
        
        setIsInitialized(true);
      }, 0);
    }
  }, [startDate, startTime, onStartTimeChange, onEndDateChange, onEndTimeChange, isInitialized]);

  // Validate time selection
  useEffect(() => {
    if (startDate && endDate && startTime && endTime) {
      const start = new Date(startDate);
      const [startHour, startMinute] = startTime.split(":").map(Number);
      start.setHours(startHour, startMinute, 0, 0);

      const end = new Date(endDate);
      const [endHour, endMinute] = endTime.split(":").map(Number);
      end.setHours(endHour, endMinute, 0, 0);

      const diffInMs = end.getTime() - start.getTime();
      const diffInHours = diffInMs / (1000 * 60 * 60);

      // Special handling for cross-midnight scenarios
      if (diffInHours < 0) {
        // Check if this is a cross-midnight scenario (same date with end time < start time)
        if (startDate === endDate) {
          const [sHour] = startTime.split(":").map(Number);
          const [eHour] = endTime.split(":").map(Number);
          
          if (eHour <= sHour) {
            // This is likely a cross-midnight booking, auto-adjust end date
            const nextDay = new Date(startDate);
            nextDay.setDate(nextDay.getDate() + 1);
            setTimeout(() => onEndDateChange(nextDay.toISOString().split('T')[0]), 0);
            return; // Exit early, let the next useEffect run handle validation
          }
        }
        setError("End date/time must be after start date/time");
        setSuccess("");
      } else if (diffInHours < 1) {
        setError("Meeting room booking requires minimum 1 hour");
        setSuccess("");
      } else if (diffInHours % 1 !== 0) {
        // Auto-adjust to nearest hour
        const adjustedHours = Math.ceil(diffInHours);
        const adjustedEndDateTime = addHoursToTime(startDate, startTime, adjustedHours);
        
        setTimeout(() => {
          onEndDateChange(adjustedEndDateTime.toISOString().split('T')[0]);
          const endHours = adjustedEndDateTime.getHours().toString().padStart(2, '0');
          onEndTimeChange(`${endHours}:00`);
        }, 0);
        
        setError("");
        setSuccess(`Duration adjusted to ${adjustedHours} hour${adjustedHours > 1 ? 's' : ''}`);
      } else {
        setError("");
        const hours = Math.floor(diffInHours);
        setSuccess(`Duration: ${hours} hour${hours > 1 ? 's' : ''}`);
      }
    }
  }, [startDate, endDate, startTime, endTime, onEndDateChange, onEndTimeChange]);

  // Update end time when start time changes
  const handleStartTimeChange = (newStartTime: string) => {
    onStartTimeChange(newStartTime);
    
    if (startDate && newStartTime) {
      const endDateTime = addHoursToTime(startDate, newStartTime, 1);
      onEndDateChange(endDateTime.toISOString().split('T')[0]);
      
      const endHours = endDateTime.getHours().toString().padStart(2, '0');
      onEndTimeChange(`${endHours}:00`);
    }
  };

  // Update end time when start date changes
  const handleStartDateChange = (newStartDate: string) => {
    onStartDateChange(newStartDate);
    
    if (startTime) {
      const endDateTime = addHoursToTime(newStartDate, startTime, 1);
      onEndDateChange(endDateTime.toISOString().split('T')[0]);
      
      const endHours = endDateTime.getHours().toString().padStart(2, '0');
      onEndTimeChange(`${endHours}:00`);
    }
  };

  // Handle manual end time changes to auto-adjust date if needed
  const handleEndTimeChange = (newEndTime: string) => {
    onEndTimeChange(newEndTime);
    
    if (startDate && startTime && newEndTime) {
      const [startHour] = startTime.split(":").map(Number);
      const [endHour] = newEndTime.split(":").map(Number);
      
      // If end time is earlier than start time, assume next day
      if (endHour <= startHour) {
        const nextDay = new Date(startDate);
        nextDay.setDate(nextDay.getDate() + 1);
        setTimeout(() => onEndDateChange(nextDay.toISOString().split('T')[0]), 0);
      } else if (endDate !== startDate) {
        // If end time is later and we're on different dates, reset to same day
        setTimeout(() => onEndDateChange(startDate), 0);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Start Date */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => handleStartDateChange(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Start Time */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Start Time</label>
          <input
            type="time"
            value={startTime}
            onChange={(e) => handleStartTimeChange(e.target.value)}
            step="3600" // 1 hour steps
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
          />
        </div>

        {/* End Date */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            min={startDate || new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
          />
        </div>

        {/* End Time */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">End Time</label>
          <input
            type="time"
            value={endTime}
            onChange={(e) => handleEndTimeChange(e.target.value)}
            step="3600" // 1 hour steps
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
          {error}
        </div>
      )}
      
      {success && (
        <div className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-md p-2">
          {success}
        </div>
      )}
    </div>
  );
}